import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import { addDays, format, differenceInDays } from "date-fns";

interface ScheduleItem {
  id: string;
  productId: number;
  productName: string;
  markNumber: string;
  quantity: number;
  tableNumber: string;
  castingDate: Date;
  priority: number;
  duration: number; // hours
  assigned: boolean;
}

interface ScheduleData {
  bomId: number | null;
  jobId: number | null;
  startDate: Date;
  endDate: Date;
  selectedProducts: number[];
  scheduleItems: ScheduleItem[];
  schedulingMode: "auto" | "manual";
  constraints: {
    maxPerDay: number;
    preferredTable: string;
    avoidWeekends: boolean;
  };
}

class ProductionScheduleWizardStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  scheduleData: ScheduleData = {
    bomId: null,
    jobId: null,
    startDate: new Date(),
    endDate: addDays(new Date(), 30),
    selectedProducts: [],
    scheduleItems: [],
    schedulingMode: "auto",
    constraints: {
      maxPerDay: 10,
      preferredTable: "",
      avoidWeekends: true,
    },
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateScheduleData = <K extends keyof ScheduleData>(
    field: K,
    value: ScheduleData[K]
  ) => {
    this.scheduleData[field] = value;
  };

  updateConstraints = <K extends keyof ScheduleData["constraints"]>(
    field: K,
    value: ScheduleData["constraints"][K]
  ) => {
    this.scheduleData.constraints[field] = value;
  };

  selectBOM = (bomId: number) => {
    const bomItemsStore = this.rootStore.bomItemsStore as ServiceStore;
    const bomItems = bomItemsStore?.entities.filter((item: any) => item.bomId === bomId) || [];

    runInAction(() => {
      this.scheduleData.bomId = bomId;
      this.scheduleData.selectedProducts = bomItems.map((item: any) => item.productId);
    });
  };

  toggleProductSelection = (productId: number) => {
    const isSelected = this.scheduleData.selectedProducts.includes(productId);
    if (isSelected) {
      this.scheduleData.selectedProducts = this.scheduleData.selectedProducts.filter(
        (id) => id !== productId
      );
    } else {
      this.scheduleData.selectedProducts = [...this.scheduleData.selectedProducts, productId];
    }
  };

  generateSchedule = () => {
    const bomItemsStore = this.rootStore.bomItemsStore as ServiceStore;
    const productsStore = this.rootStore.productsStore as ServiceStore;
    const castingMapLayoutStore = this.rootStore.castingMapLayoutStore as ServiceStore;

    const bomItems = bomItemsStore?.entities.filter(
      (item: any) =>
        item.bomId === this.scheduleData.bomId &&
        this.scheduleData.selectedProducts.includes(item.productId)
    ) || [];

    const tables = castingMapLayoutStore?.entities || [];
    const availableTables = tables.filter((t: any) => t.status === "available");

    let currentDate = new Date(this.scheduleData.startDate);
    let tableIndex = 0;
    const scheduleItems: ScheduleItem[] = [];

    // Auto-generate schedule based on constraints
    bomItems.forEach((item: any, index: number) => {
      const product = productsStore?.entities.find((p: any) => p.id === item.productId);

      for (let i = 0; i < item.quantity; i++) {
        // Skip weekends if needed
        if (this.scheduleData.constraints.avoidWeekends) {
          while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
            currentDate = addDays(currentDate, 1);
          }
        }

        scheduleItems.push({
          id: `${item.id}-${i}`,
          productId: item.productId,
          productName: product?.name || "Unknown",
          markNumber: product?.markNumber || `M${index + 1}`,
          quantity: 1,
          tableNumber: availableTables[tableIndex % availableTables.length]?.tableNumber || "T1",
          castingDate: new Date(currentDate),
          priority: index + 1,
          duration: 8, // Default 8 hours
          assigned: false,
        });

        // Move to next table
        tableIndex++;

        // Move to next day after max items per day
        if ((tableIndex % this.scheduleData.constraints.maxPerDay) === 0) {
          currentDate = addDays(currentDate, 1);
        }
      }
    });

    runInAction(() => {
      this.scheduleData.scheduleItems = scheduleItems;
      this.nextStep();
    });
  };

  updateScheduleItem = (itemId: string, field: string, value: any) => {
    this.scheduleData.scheduleItems = this.scheduleData.scheduleItems.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Job & BOM Selection
        if (!this.scheduleData.jobId) {
          this.setError("Please select a job");
          return false;
        }
        if (!this.scheduleData.bomId) {
          this.setError("Please select a Bill of Materials");
          return false;
        }
        if (this.scheduleData.selectedProducts.length === 0) {
          this.setError("Please select at least one product to schedule");
          return false;
        }
        break;

      case 1: // Schedule Parameters
        if (this.scheduleData.endDate <= this.scheduleData.startDate) {
          this.setError("End date must be after start date");
          return false;
        }
        if (this.scheduleData.constraints.maxPerDay <= 0) {
          this.setError("Maximum items per day must be greater than 0");
          return false;
        }
        break;

      case 2: // Generated Schedule Review
        if (this.scheduleData.scheduleItems.length === 0) {
          this.setError("No schedule items generated. Please go back and check your parameters.");
          return false;
        }
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const billOfMaterialsStore = this.rootStore.billOfMaterialsStore as ServiceStore;
      const bomItemsStore = this.rootStore.bomItemsStore as ServiceStore;
      const productsStore = this.rootStore.productsStore as ServiceStore;
      const castingMapLayoutStore = this.rootStore.castingMapLayoutStore as ServiceStore;

      await Promise.all([
        jobStore?.find({ query: { archived: false } }),
        billOfMaterialsStore?.find({ query: { active: true } }),
        bomItemsStore?.find(),
        productsStore?.find(),
        castingMapLayoutStore?.find(),
      ]);
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  createSchedule = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const jobScheduleStore = this.rootStore.jobScheduleStore as ServiceStore;
      const panelStore = this.rootStore.panelStore as ServiceStore;

      // Group items by date for batch creation
      const itemsByDate = this.scheduleData.scheduleItems.reduce((acc: any, item) => {
        const dateKey = format(item.castingDate, "yyyy-MM-dd");
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(item);
        return acc;
      }, {});

      // Create schedule entries
      for (const [date, items] of Object.entries(itemsByDate)) {
        // Create job schedule entry
        const schedule = await jobScheduleStore?.create({
          jobId: this.scheduleData.jobId,
          scheduleDate: date,
          scheduledQuantity: (items as ScheduleItem[]).length,
          status: "scheduled",
          notes: `Auto-generated schedule for ${(items as ScheduleItem[]).length} items`,
        });

        // Create panel records for each item
        for (const item of items as ScheduleItem[]) {
          await panelStore?.create({
            productId: item.productId,
            jobId: this.scheduleData.jobId,
            panelNumber: `${item.markNumber}-${Date.now()}`,
            markNumber: item.markNumber,
            tableNumber: item.tableNumber,
            castingDate: format(item.castingDate, "yyyy-MM-dd"),
            status: "Scheduled",
            priority: item.priority,
            productionDuration: item.duration,
            qcStatus: "pending",
          });
        }
      }

      runInAction(() => {
        this.setSuccess("Production schedule created successfully!");
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to create schedule");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.scheduleData = {
      bomId: null,
      jobId: null,
      startDate: new Date(),
      endDate: addDays(new Date(), 30),
      selectedProducts: [],
      scheduleItems: [],
      schedulingMode: "auto",
      constraints: {
        maxPerDay: 10,
        preferredTable: "",
        avoidWeekends: true,
      },
    };
  };

  // Computed values
  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get boms() {
    const billOfMaterialsStore = this.rootStore.billOfMaterialsStore as ServiceStore;
    return billOfMaterialsStore?.entities || [];
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get tables() {
    const castingMapLayoutStore = this.rootStore.castingMapLayoutStore as ServiceStore;
    return castingMapLayoutStore?.entities || [];
  }

  get selectedBOM() {
    if (!this.scheduleData.bomId) return null;
    return this.boms.find((b: any) => b.id === this.scheduleData.bomId);
  }

  get bomItems() {
    if (!this.scheduleData.bomId) return [];
    const bomItemsStore = this.rootStore.bomItemsStore as ServiceStore;
    return bomItemsStore?.entities.filter((item: any) => item.bomId === this.scheduleData.bomId) || [];
  }

  get totalScheduledItems() {
    return this.scheduleData.scheduleItems.length;
  }

  get scheduleDuration() {
    if (this.scheduleData.scheduleItems.length === 0) return 0;
    const lastItem = this.scheduleData.scheduleItems[this.scheduleData.scheduleItems.length - 1];
    const firstItem = this.scheduleData.scheduleItems[0];
    return differenceInDays(lastItem?.castingDate || new Date(), firstItem?.castingDate || new Date()) + 1;
  }

  get usedTables() {
    return [...new Set(this.scheduleData.scheduleItems.map((i) => i.tableNumber))].length;
  }

  get canProceedFromBOMSelection() {
    return !!this.scheduleData.bomId && this.scheduleData.selectedProducts.length > 0;
  }

  get canProceedFromParameterSetup() {
    return true; // Parameters are optional
  }
}

export default ProductionScheduleWizardStore;